import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import AppModal from "components/ui/AppModal";
import AppCheckbox from "components/ui/AppCheckbox";
import AppSearchInput from "components/ui/AppSearchInput";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import AppButton from "components/ui/AppButton";
import EmptyFooter from "components/EmptyFooter";
import { UTILS } from "utils";
import { CONSTANTS as C } from "config";

import { errorHandler } from "api/api/errorHandler";
import useGetBRandItems from "../hooks/useGetBRandItems";
import { API } from "api";

const itemThumbSrc = (row) => {
  const urls = row?.itemImageURLs;
  const file = Array.isArray(urls) && urls.length ? urls[urls.length - 1] : null;
  return file || C.PLACEHOLDER_IMAGE;
};

const LinkItemWithCategoryModal = ({
  cat,
  onClose,
  menuId,
  brandItems,
  getCategoryInfo,
  items,
  open,
}) => {
  const getItemsByBrand = useGetBRandItems();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const handleRowToggle = useCallback((item) => {
    setCheckedRows((prev) => {
      const next = [...prev];
      const i = next.findIndex((row) => row?.itemId === item?._id);
      if (i !== -1) next.splice(i, 1);
      else next.push({ itemId: item?._id });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setCheckedRows([]);
      setSearchTerm("");
      setCurrentPage(1);
      setPageSize(5);
    }
  }, [open]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!open) return;
    getItemsByBrand({ silent: true });
  }, [open, getItemsByBrand]);

  const getUnattachedItems = (linkedItems) => {
    const attachedItemsLookup = _.keyBy(
      linkedItems,
      (item) => item?.itemId?._id
    );
    return brandItems?.filter((item) => !attachedItemsLookup[item?._id]) ?? [];
  };

  const unlinkedItems = useMemo(() => {
    const list = getUnattachedItems(items);
    if (!searchTerm?.trim()) return list;
    const q = searchTerm.trim().toLowerCase();
    return list.filter((item) =>
      item?.baseLanguage?.title?.toLowerCase()?.includes(q)
    );
  }, [items, searchTerm, brandItems]);

  const columns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "_id",
        key: "select",
        width: 56,
        render: (_, row) => (
          <div className="flex justify-center">
            <AppCheckbox
              hideLabel
              checked={checkedRows.some((r) => r?.itemId === row?._id)}
              onChange={() => handleRowToggle(row)}
            />
          </div>
        ),
      },
      {
        title: "",
        dataIndex: "itemImageURLs",
        key: "image",
        width: 88,
        render: (_, row) => (
          <img
            alt=""
            src={itemThumbSrc(row)}
            className="h-14 w-14 shrink-0 rounded-xl object-cover"
          />
        ),
      },
      {
        title: t("Name"),
        dataIndex: "baseLanguage",
        key: "name",
        render: (value) => (
          <span className="block min-w-0 truncate font-semibold text-gray-900">
            {value?.title}
          </span>
        ),
      },
      {
        title: t("Description"),
        dataIndex: "baseLanguage",
        key: "description",
        render: (value) => (
          <span className="block min-w-0 break-words text-gray-600 line-clamp-3">
            {value?.description}
          </span>
        ),
      },
      {
        title: t("Price"),
        dataIndex: "price",
        key: "price",
        width: 100,
        render: (value) => (
          <span className="whitespace-nowrap font-medium">
            {t("AED")} {value}
          </span>
        ),
      },
    ],
    [checkedRows, handleRowToggle, t]
  );

  const handlePageChange = (page, ps) => {
    setCurrentPage(page);
    setPageSize(ps);
  };

  const linkItemWithMenuCategory = async () => {
    try {
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.linkItemWithCategory({
        brandId: userDetail.brandId,
        categoryId: cat?._id,
        menuId,
        items: checkedRows,
      });
      if (result?.success === 1) {
        await getCategoryInfo(cat?._id, { silent: true });
        UTILS.successNotification(result?.message);
        onClose();
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const hasLinkableRows = unlinkedItems?.length >= 1;

  const footer = (
    <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
      <AppButton variant="outline" size="lg" onClick={onClose} disabled={loading}>
        {t("Cancel")}
      </AppButton>
      {hasLinkableRows ? (
        <AppButton
          variant="primary"
          size="lg"
          disabled={checkedRows?.length <= 0 || loading}
          loading={loading}
          onClick={linkItemWithMenuCategory}
        >
          {t("Link Item")}
        </AppButton>
      ) : null}
    </div>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      footer={footer}
      bodyStyle={{
        overflow: "hidden",
        paddingTop: 8,
        paddingBottom: 0,
      }}
    >
      <div className="flex w-full flex-col gap-3 bg-white text-left">
        <header className="flex flex-row flex-wrap items-center justify-start gap-3 border-b border-gray-100 pb-4">
          <h2 className="m-0 min-w-0 text-20px font-semibold leading-tight text-gray-900">
            {t("Add Items to")}{" "}
            {cat?.baseLanguage?.name || ""}
          </h2>
          <div className="w-full min-w-[200px] shrink-0 sm:w-auto sm:max-w-[260px]">
            <AppSearchInput
              value={searchTerm}
              onInput={(e) => setSearchTerm(e.target.value)}
              placeholder={t("Search by name")}
            />
          </div>
        </header>

        {hasLinkableRows ? (
          <div
            className="flex min-h-[200px] flex-col overflow-hidden"
            style={{
              height: "min(520px, calc(100vh - 320px))",
            }}
          >
            <AppPaginatedTable
              fitWithinContainer
              columns={columns}
              data={unlinkedItems}
              clientSidePagination
              page={currentPage}
              pageSize={pageSize}
              total={unlinkedItems.length}
              onPageChange={handlePageChange}
              rowOptions={[5, 10, 20, 40]}
              rowIndexKey="_id"
            />
          </div>
        ) : (
          <EmptyFooter
            url="/app/dashboard/createitem"
            heading={t("No Item")}
            heading2={t("Add New Item")}
            handleClick={onClose}
          />
        )}
      </div>
    </AppModal>
  );
};

export default LinkItemWithCategoryModal;

LinkItemWithCategoryModal.propTypes = {
  onClose: PropTypes.func,
  cat: PropTypes.object,
  menuId: PropTypes.string,
  brandItems: PropTypes.array,
  getCategoryInfo: PropTypes.func,
  items: PropTypes.array,
  open: PropTypes.bool,
};
